import React, { Fragment, useRef } from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import { type AnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/new';
import Form, { ErrorMessage, Field, MessageWrapper } from '@atlaskit/form';
import Modal, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import TextField from '@atlaskit/textfield';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';

import { useConversationModal } from '../../../hooks/conversation-modal';

import messages from './messages';

type FormFields = {
	name: string;
};

type EditConversationModalProps = {
	onEditConfirm: (name: string) => Promise<void | {
		error: string;
	}>;
};

export const EditConversationModal = ({
	onEditConfirm,
}: EditConversationModalProps): React.JSX.Element => {
	di(useConversationModal);

	const { sendAnalyticsEvent } = useAnalytics();
	const { formatMessage } = useIntl();
	const { isOpen, conversation, mode, closeModal } = useConversationModal();
	const isModalOpen = isOpen && mode === 'edit';
	const nameInputRef = useRef<HTMLInputElement>(null);

	const onSubmit = async (fields: FormFields) => {
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'editConversationModalSaveButton',
			attributes: {
				conversationId: conversation.id,
			},
		});

		if (fields.name?.toLocaleLowerCase().trim() === conversation.name.toLocaleLowerCase().trim()) {
			return { name: formatMessage(messages.nameIsSameErrorMessage) };
		}

		try {
			const result = await onEditConfirm(fields.name.trim());

			if (result?.error) {
				return { name: result.error };
			}

			closeModal();
		} catch {
			return { name: formatMessage(messages.genericError) };
		}
	};

	const onCancel = (e: AnalyticsEvent) => {
		sendAnalyticsEvent(
			e.update({
				actionSubjectId: 'editConversationModalCancelButton',
				attributes: {
					conversationId: conversation.id,
				},
			}),
		);

		closeModal();
	};

	return (
		<ModalTransition>
			{isModalOpen && (
				<Modal
					autoFocus={nameInputRef}
					shouldCloseOnEscapePress={false}
					onClose={(_, e) => onCancel(e)}
					width="small"
					testId="editConversationModal"
				>
					<Form<FormFields> onSubmit={onSubmit}>
						{({ formProps, submitting }) => (
							<form {...formProps}>
								<ModalHeader hasCloseButton>
									<ModalTitle>{formatMessage(messages.modalTitle)}</ModalTitle>
								</ModalHeader>
								<ModalBody>
									<Field
										name="name"
										label={formatMessage(messages.editNameLabel)}
										isRequired
										defaultValue={conversation?.name}
									>
										{({ fieldProps, error }) => (
											<Fragment>
												<TextField
													defaultValue={conversation.name}
													{...fieldProps}
													ref={nameInputRef}
												/>
												<MessageWrapper>
													{error && <ErrorMessage>{error}</ErrorMessage>}
												</MessageWrapper>
											</Fragment>
										)}
									</Field>
								</ModalBody>
								<ModalFooter>
									<Button appearance="subtle" onClick={(_, e) => onCancel(e)}>
										{formatMessage(messages.cancelButtonLabel)}
									</Button>
									<Button isLoading={submitting} appearance="primary" type="submit">
										{formatMessage(messages.saveButtonLabel)}
									</Button>
								</ModalFooter>
							</form>
						)}
					</Form>
				</Modal>
			)}
		</ModalTransition>
	);
};
